import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCw, 
  Search, 
  Server, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import StationCard from './StationCard';
import UnifiedStationActions from './UnifiedStationActions';
import FonteyneLogo from './FonteyneLogo';
import { useStations } from '../hooks/useStations';
import { wsService } from '../services/websocket';
import api from '../services/api';

const Dashboard = () => {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedStation, setSelectedStation] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  const { 
    stations, 
    loading, 
    error, 
    lastUpdate, 
    refresh,
    getOnlineStations,
    getOfflineStations,
    getCriticalStations,
    getStationsCount
  } = useStations({});

  // Vérifier la validité de la session au chargement de la page
  useEffect(() => {
    const checkSessionValidity = async () => {
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        navigate('/login');
        return;
      }

      try {
        // Vérifier la validité du token en testant une route protégée légère
        await api.get('/api/v1/stations', { 
          params: { limit: 1 },
          timeout: 5000 
        });
      } catch (error) {
        // Si erreur 401 ou autre erreur d'authentification, rediriger vers login
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('adminToken');
          navigate('/login');
        }
      }
    };

    // Vérifier immédiatement au chargement
    checkSessionValidity();

    // Vérifier périodiquement toutes les 5 minutes
    const intervalId = setInterval(checkSessionValidity, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [navigate]);

  // Connexion WebSocket
  useEffect(() => {
    // Ne connecter le WebSocket que si authentifié
    if (!isAuthenticated) {
      return;
    }

    wsService.connect();
    
    // Écouter les changements de connexion
    const handleConnection = (data) => {
      setWsConnected(data.status === 'connected');
    };
    
    wsService.on('connection', handleConnection);
    
    return () => {
      wsService.off('connection', handleConnection);
    };
  }, [isAuthenticated]);

  // Filtrage des stations
  const filteredStations = stations.filter(station => {
    const matchesSearch = station.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         station.station_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (station.location && station.location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || station.status === statusFilter;
    const matchesType = typeFilter === 'all' || station.station_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Statistiques
  const stats = {
    total: getStationsCount(),
    online: getOnlineStations().length,
    offline: getOfflineStations().length,
    critical: getCriticalStations().length,
  };

  const getStatusCount = (status) => {
    return stations.filter(s => s.status === status).length;
  };

  return (
    <div className="min-h-screen bg-fonteyne-warm-gray">
      {/* Header Premium Fonteyne Style */}
      <div className="bg-fonteyne-white shadow-lg border-b-2 border-accent-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            {/* Logo et Branding */}
            <div className="flex items-center space-x-6">
              <FonteyneLogo className="h-10 w-auto" />
              <div className="border-l-2 border-primary-200 pl-6">
                <h1 className="text-2xl font-display font-bold text-fonteyne-black tracking-wide">
                  Gestion de Flotte
                </h1>
                <p className="text-fonteyne-medium-gray font-medium text-sm tracking-wide uppercase">
                  Stations IoTBox Centralisées
                </p>
              </div>
            </div>
            
            {/* Actions et Statut */}
            <div className="flex items-center space-x-6">
              {/* Indicateur WebSocket Premium */}
              <div className="flex items-center space-x-3 px-4 py-2 rounded-lg bg-primary-50 border border-primary-200">
                <div className={`h-3 w-3 rounded-full ${wsConnected ? 'bg-success-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-fonteyne-medium-gray uppercase tracking-wide">
                    Connexion
                  </span>
                  <span className={`text-sm font-semibold ${wsConnected ? 'text-success-600' : 'text-gray-600'}`}>
                    {wsConnected ? 'Active' : 'Interrompue'}
                  </span>
                </div>
              </div>
              
              {/* Bouton actualiser élégant */}
              <button
                onClick={refresh}
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
                title="Actualiser les données"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Actualiser</span>
              </button>

              {/* Bouton déconnexion premium */}
              <button
                onClick={logout}
                className="btn-secondary flex items-center space-x-2 border-2 border-primary-300 hover:border-primary-400"
                title="Se déconnecter"
              >
                <LogOut className="h-4 w-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Server className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Stations</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">En Ligne</p>
                <p className="text-2xl font-semibold text-green-600">{stats.online}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Critiques</p>
                <p className="text-2xl font-semibold text-yellow-600">{stats.critical}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Hors Ligne</p>
                <p className="text-2xl font-semibold text-red-600">{stats.offline}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une station..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtre par statut */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="online">En ligne ({getStatusCount('online')})</option>
                <option value="healthy">Sain ({getStatusCount('healthy')})</option>
                <option value="degraded">Dégradé ({getStatusCount('degraded')})</option>
                <option value="critical">Critique ({getStatusCount('critical')})</option>
                <option value="offline">Hors ligne ({getStatusCount('offline')})</option>
              </select>
            </div>

            {/* Filtre par type */}
            <div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous les types</option>
                <option value="pos">POS</option>
                <option value="selfscan">Self-Scan</option>
                <option value="kitchen">Cuisine</option>
              </select>
            </div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Liste des stations */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Chargement des stations...</p>
          </div>
        ) : filteredStations.length === 0 ? (
          <div className="text-center py-12">
            <Server className="h-12 w-12 mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">
              {stations.length === 0 ? 'Aucune station trouvée' : 'Aucune station ne correspond aux filtres'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStations.map((station) => (
                <StationCard
                  key={station.station_id}
                  station={station}
                  onSelect={setSelectedStation}
                />
              ))}
            </div>

            {/* Informations de mise à jour */}
            {lastUpdate && (
              <div className="mt-8 text-center text-sm text-gray-500">
                <Clock className="h-4 w-4 inline mr-1" />
                Dernière mise à jour: {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal détails de station avec actions */}
      {selectedStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Détails de la station</h2>
                <button
                  onClick={() => setSelectedStation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Informations de base */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium border-b pb-2">Informations</h3>
                  <div className="space-y-2">
                    <p><strong>ID:</strong> {selectedStation.station_id}</p>
                    <p><strong>Nom:</strong> {selectedStation.name}</p>
                    <p><strong>Statut:</strong> <span className={`status-${selectedStation.status}`}>{selectedStation.status}</span></p>
                    <p><strong>Localisation:</strong> {selectedStation.location || 'Non définie'}</p>
                    <p><strong>Type:</strong> {selectedStation.station_type}</p>
                    <p><strong>Dernière vue:</strong> {selectedStation.last_seen ? new Date(selectedStation.last_seen).toLocaleString() : 'Jamais'}</p>
                  </div>
                  
                  {/* Services Status */}
                  {selectedStation.services && Object.keys(selectedStation.services).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Services:</h4>
                      <div className="space-y-1">
                        {Object.entries(selectedStation.services).map(([serviceName, serviceStatus]) => (
                          <div key={serviceName} className="flex justify-between items-center">
                            <span className="text-sm">{serviceName}</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              serviceStatus.container_running ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {serviceStatus.container_running ? 'Actif' : 'Arrêté'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Actions Panel Unifié */}
                <div className="lg:col-span-2">
                  <UnifiedStationActions 
                    station={selectedStation} 
                    onClose={() => setSelectedStation(null)} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

